// Impact Plexus Mock Data
// Realistic data for Canadian nonprofits using actual terminology from the platform

export const ENTITY_STATS = {
  totalEntities: 83301,
  totalGrantPrograms: 605,
  totalGivers: 38654,
  avgMatchScore: 0.73,
};

// Sample grant opportunities with 8-measures
export const GRANT_OPPORTUNITIES = [
  {
    id: "grant-001",
    programName: "Community Resilience Fund",
    funderName: "Ontario Trillium Foundation",
    totalAmount: 250000,
    deadline: "2026-03-15",
    matchScore: 0.92,
    measures: {
      opportunities: 250000,
      expectations: 172500,
      survey: 45000,
      outlook: 12.5,
      leverage: 0.87,
      positioning: 82,
      efficiency: 8625,
      assurance: 155250,
    },
    confidenceInterval: { low: 0.85, mid: 0.92, high: 0.96 },
    status: "qualified",
    sector: "Community Development",
    region: "Ontario",
  },
  {
    id: "grant-002",
    programName: "Youth Mental Health Initiative",
    funderName: "Bell Let's Talk",
    totalAmount: 175000,
    deadline: "2026-02-28",
    matchScore: 0.88,
    measures: {
      opportunities: 175000,
      expectations: 140000,
      survey: 35000,
      outlook: 8.2,
      leverage: 0.79,
      positioning: 75,
      efficiency: 7000,
      assurance: 126000,
    },
    confidenceInterval: { low: 0.81, mid: 0.88, high: 0.94 },
    status: "qualified",
    sector: "Health & Wellness",
    region: "National",
  },
  {
    id: "grant-003",
    programName: "Indigenous Community Support",
    funderName: "Vancouver Foundation",
    totalAmount: 500000,
    deadline: "2026-04-30",
    matchScore: 0.85,
    measures: {
      opportunities: 500000,
      expectations: 375000,
      survey: 83333,
      outlook: 15.3,
      leverage: 0.91,
      positioning: 88,
      efficiency: 12500,
      assurance: 337500,
    },
    confidenceInterval: { low: 0.78, mid: 0.85, high: 0.91 },
    status: "qualified",
    sector: "Indigenous Services",
    region: "British Columbia",
  },
  {
    id: "grant-004",
    programName: "Environmental Sustainability Grant",
    funderName: "David Suzuki Foundation",
    totalAmount: 120000,
    deadline: "2026-05-15",
    matchScore: 0.76,
    measures: {
      opportunities: 120000,
      expectations: 84000,
      survey: 24000,
      outlook: 6.8,
      leverage: 0.72,
      positioning: 68,
      efficiency: 5600,
      assurance: 75600,
    },
    confidenceInterval: { low: 0.69, mid: 0.76, high: 0.83 },
    status: "qualified",
    sector: "Environment",
    region: "National",
  },
  {
    id: "grant-005",
    programName: "Arts & Culture Recovery",
    funderName: "Canada Council for the Arts",
    totalAmount: 85000,
    deadline: "2026-03-01",
    matchScore: 0.71,
    measures: {
      opportunities: 85000,
      expectations: 55250,
      survey: 17000,
      outlook: 4.2,
      leverage: 0.65,
      positioning: 62,
      efficiency: 4600,
      assurance: 49725,
    },
    confidenceInterval: { low: 0.64, mid: 0.71, high: 0.78 },
    status: "near-miss",
    sector: "Arts & Culture",
    region: "National",
  },
];

// Near-miss alerts with repositioning suggestions
export const NEAR_MISS_ALERTS = [
  {
    id: "nm-001",
    programName: "Arts & Culture Recovery",
    funderName: "Canada Council for the Arts",
    matchScore: 0.71,
    gaps: [
      {
        type: "revenue",
        current: "$180,000",
        required: "$250,000 minimum",
        actionable: true,
        effort: "medium",
        timeToModify: "6-12 months",
      },
      {
        type: "track_record",
        current: "2 years active",
        required: "3+ years track record",
        actionable: true,
        effort: "low",
        timeToModify: "12 months",
      },
    ],
    repositioningSteps: [
      "Partner with established arts organization for joint applications",
      "Apply for smaller capacity-building grants to build track record",
      "Diversify revenue through community fundraising events",
    ],
    distanceToQualify: 0.14,
  },
  {
    id: "nm-002",
    programName: "Tech for Good Initiative",
    funderName: "TELUS Friendly Future Foundation",
    matchScore: 0.68,
    gaps: [
      {
        type: "sector_alignment",
        current: "Community Development",
        required: "Technology or Digital Literacy focus",
        actionable: true,
        effort: "high",
        timeToModify: "3-6 months",
      },
    ],
    repositioningSteps: [
      "Develop digital literacy component within existing programs",
      "Partner with tech-focused nonprofit for collaborative proposal",
      "Highlight any existing technology-enabled service delivery",
    ],
    distanceToQualify: 0.17,
  },
];

// AdminHub pending actions
export const PENDING_ACTIONS = [
  {
    id: "action-001",
    type: "follow_up",
    title: "Follow up with Ontario Trillium Foundation",
    description: "Application submitted 14 days ago - follow up recommended",
    dueDate: "2026-01-18",
    priority: "high",
    grant: "Community Resilience Fund",
    status: "pending",
  },
  {
    id: "action-002",
    type: "compliance",
    title: "Q4 Progress Report Due",
    description: "Submit quarterly report for Youth Mentorship Program",
    dueDate: "2026-01-31",
    priority: "urgent",
    grant: "Youth Mentorship Program",
    status: "pending",
  },
  {
    id: "action-003",
    type: "outreach",
    title: "Draft outreach to Vancouver Foundation",
    description: "AI-generated draft ready for review",
    dueDate: "2026-01-25",
    priority: "medium",
    grant: "Indigenous Community Support",
    status: "draft_ready",
  },
  {
    id: "action-004",
    type: "deadline",
    title: "Application deadline approaching",
    description: "Bell Let's Talk Youth Mental Health Initiative",
    dueDate: "2026-02-28",
    priority: "medium",
    grant: "Youth Mental Health Initiative",
    status: "in_progress",
  },
  {
    id: "action-005",
    type: "compliance",
    title: "Budget reallocation approval needed",
    description: "Request approval for 15% budget shift to digital services",
    dueDate: "2026-02-05",
    priority: "medium",
    grant: "Digital Inclusion Program",
    status: "pending",
  },
];

// Compliance calendar items
export const COMPLIANCE_ITEMS = [
  {
    id: "comp-001",
    grantName: "Youth Mentorship Program",
    funder: "RBC Foundation",
    condition: "Quarterly Progress Report",
    deadline: "2026-01-31",
    status: "upcoming",
    daysRemaining: 16,
  },
  {
    id: "comp-002",
    grantName: "Community Resilience Fund",
    funder: "Ontario Trillium Foundation",
    condition: "Mid-term Evaluation",
    deadline: "2026-03-15",
    status: "on_track",
    daysRemaining: 59,
  },
  {
    id: "comp-003",
    grantName: "Digital Inclusion Program",
    funder: "Microsoft Philanthropies",
    condition: "Annual Financial Audit",
    deadline: "2026-02-28",
    status: "in_progress",
    daysRemaining: 43,
  },
];

// ImpactGrow data
export const IMPACT_FRAMEWORK = {
  overallScore: 78,
  level: "Growing",
  nextLevel: "Established",
  pointsToNext: 12,
  lenses: {
    individual: {
      score: 82,
      metrics: ["Beneficiary satisfaction: 4.2/5", "Skill improvement: 67%", "Employment outcomes: 45%"],
    },
    government: {
      score: 75,
      metrics: ["Policy alignment: 3 initiatives", "Cost savings: $120K annually", "Service gap coverage: 85%"],
    },
    business: {
      score: 71,
      metrics: ["CSR partnerships: 2 active", "Volunteer hours: 450/year", "In-kind donations: $35K"],
    },
    community: {
      score: 84,
      metrics: ["Community reach: 2,400 people", "Partnership network: 12 orgs", "Local economic impact: $280K"],
    },
  },
  milestones: [
    { title: "First Grant Secured", achieved: true, date: "2024-06-15" },
    { title: "$100K Total Funding", achieved: true, date: "2025-03-20" },
    { title: "Impact Framework Complete", achieved: false, progress: 78 },
    { title: "5-Year Track Record", achieved: false, progress: 60 },
  ],
};

// ResourceHub data
export const SOFTWARE_RESOURCES = [
  {
    id: "sw-001",
    name: "Google for Nonprofits",
    category: "Productivity",
    value: "$2,400/year",
    description: "Google Workspace, Google Ad Grants, YouTube for Nonprofits",
    eligibility: "Registered Canadian charity",
    recommended: true,
  },
  {
    id: "sw-002",
    name: "Microsoft 365 for Nonprofits",
    category: "Productivity",
    value: "$3,600/year",
    description: "Office 365 E1, Teams, SharePoint, OneDrive",
    eligibility: "Registered charity with <300 employees",
    recommended: true,
  },
  {
    id: "sw-003",
    name: "Salesforce NPSP",
    category: "CRM",
    value: "$18,000/year",
    description: "10 free licenses, donor management, volunteer tracking",
    eligibility: "501(c)(3) equivalent",
    recommended: false,
  },
  {
    id: "sw-004",
    name: "Canva for Nonprofits",
    category: "Design",
    value: "$156/year",
    description: "Full Canva Pro access for entire team",
    eligibility: "Registered nonprofit",
    recommended: true,
  },
  {
    id: "sw-005",
    name: "Slack for Nonprofits",
    category: "Communication",
    value: "$1,020/year",
    description: "85% discount on paid plans",
    eligibility: "Verified nonprofit status",
    recommended: false,
  },
];

export const PARTNERSHIP_OPPORTUNITIES = [
  {
    id: "part-001",
    organization: "Youth Services Bureau of Ottawa",
    type: "Collaboration",
    alignment: 0.89,
    opportunity: "Joint youth mental health program delivery",
    region: "Ottawa, ON",
  },
  {
    id: "part-002",
    organization: "University of Toronto - Social Work",
    type: "Research",
    alignment: 0.82,
    opportunity: "Impact evaluation research partnership",
    region: "Toronto, ON",
  },
  {
    id: "part-003",
    organization: "City of Vancouver - Social Planning",
    type: "Co-funding",
    alignment: 0.76,
    opportunity: "Municipal co-funded housing support program",
    region: "Vancouver, BC",
  },
];

// DataIntel insights
export const DATA_INSIGHTS = [
  {
    id: "insight-001",
    type: "trend",
    title: "Youth Services Funding Up 23%",
    description: "Grant programs targeting youth services have increased funding by 23% YoY across Canadian foundations.",
    impact: "high",
    relevance: 0.92,
    date: "2026-01-10",
  },
  {
    id: "insight-002",
    type: "opportunity",
    title: "New Mental Health Initiative Launched",
    description: "TD Bank Group announces $50M mental health funding program for Canadian nonprofits.",
    impact: "high",
    relevance: 0.88,
    date: "2026-01-08",
  },
  {
    id: "insight-003",
    type: "benchmark",
    title: "Your Revenue Growth Exceeds Sector Average",
    description: "Your organization's 18% revenue growth outpaces the sector average of 7.2%.",
    impact: "medium",
    relevance: 0.85,
    date: "2026-01-05",
  },
  {
    id: "insight-004",
    type: "alert",
    title: "Deadline Clustering Detected",
    description: "5 relevant grant deadlines fall within March 1-15. Consider prioritizing applications.",
    impact: "medium",
    relevance: 0.95,
    date: "2026-01-12",
  },
];

// Entity analytics for DataIntel
export const ENTITY_ANALYTICS = {
  profile: {
    bn: "123456789RR0001",
    name: "Community Youth Services of Ontario",
    designation: "A",
    sector: "Social Services",
    region: "Ontario",
    founded: 2019,
    revenue: 485000,
    overheadRatio: 0.18,
    monthsReserves: 4.2,
  },
  giveSide: {
    totalGiven5yr: 0,
    consistency: 0,
    isGiver: false,
  },
  receiveSide: {
    totalReceived5yr: 1250000,
    uniqueFunders: 8,
    trackRecordScore: 0.72,
    repeatFundingRate: 0.63,
  },
  featureScores: {
    financialHealth: 0.78,
    capacity: 0.71,
    riskFlags: 0.12,
    trackRecord: 0.72,
    sectorAlignment: 0.85,
  },
  goalHierarchy: {
    L1: "Empower youth to achieve their full potential",
    L2: ["Mental health support", "Education assistance", "Employment readiness"],
    L3: ["One-on-one counseling", "Tutoring programs", "Job skills workshops"],
    L4: ["Licensed counselors", "Learning management system", "Employer network"],
  },
};

// Dashboard summary metrics
export const DASHBOARD_SUMMARY = {
  fundMatch: {
    expectedOpportunities: 1480000,
    qualifiedGrants: 12,
    avgMatchScore: 0.81,
  },
  adminHub: {
    pendingActions: 7,
    urgentItems: 2,
    completedThisWeek: 5,
  },
  impactGrow: {
    currentLevel: "Growing",
    score: 78,
    nextStep: "Complete business partnership assessment",
  },
  resourceHub: {
    newResources: 3,
    potentialSavings: 25176,
    matchedPartners: 4,
  },
  dataIntel: {
    newInsights: 4,
    dataFreshness: 0.94,
    profileCompleteness: 0.87,
  },
};

// Chart data for visualizations
export const FUNDING_TREND_DATA = [
  { month: "Aug", received: 45000, projected: 42000 },
  { month: "Sep", received: 62000, projected: 58000 },
  { month: "Oct", received: 78000, projected: 75000 },
  { month: "Nov", received: 95000, projected: 92000 },
  { month: "Dec", received: 115000, projected: 110000 },
  { month: "Jan", received: 125000, projected: 128000 },
];

export const SECTOR_DISTRIBUTION = [
  { name: "Social Services", value: 35, color: "#14b8a6" },
  { name: "Health & Wellness", value: 25, color: "#FBBF24" },
  { name: "Education", value: 20, color: "#06B6D4" },
  { name: "Environment", value: 12, color: "#3B82F6" },
  { name: "Arts & Culture", value: 8, color: "#8B5CF6" },
];
